/*!
 * OnSchedJ (http://onsched.com)
 * Copyright 2014-2020 OnSched
 */

'use strict';
import moment from 'moment';
import momenttimezone from 'moment-timezone';

import './assets/scss/onsched.scss';
import './assets/scss/onsched.sass';

import { OnSchedMount }     from './OnSchedMount'
import { OnSchedRest }      from './OnSchedRest'
import { OnSchedOnChange }  from './utils/OnSchedOnChange'
import { OnSchedOnClick }   from './utils/OnSchedOnClick'
import { OnSchedHelpers }   from './utils/OnSchedHelpers'
import { OnSchedTemplates } from './OnSchedTemplates'
 

// Main entry point for OnSched.js

function OnSched(ClientId, Environment, Options) {

    // Should probably include scope in an Options object
    var self = {};
    self.objectName = "OnSched";
    self.scope = Options === undefined ? "OnSchedApi" : Options.scope;
    self.access_token = Options?.access_token;
    self.locale = Options === undefined || Options.locale == null ? "en-US" : Options.locale;
    self.theme = Options?.theme?.length > 0 ? Options.theme : 'primary';
    self.clientId = ClientId;
    self.environment = Environment === null ? "live" : Environment;
    // self.environment ="dev"
    // console.log('OnSched Environment', Environment)
    // console.log('OnSched self.env before', self.environment)

    switch (self.environment) {
        case "live":
            self.apiBaseUrl      = 'https://js.onsched.com/api/onsched/consumer';
            self.setupApiBaseUrl = 'https://js.onsched.com/api/onsched/setup';
            break;
        case "sbox":
            self.apiBaseUrl      = 'https://sandbox-js.onsched.com/api/onsched/consumer';
            self.setupApiBaseUrl = 'https://sandbox-js.onsched.com/api/onsched/setup';
            break;
        // case "dev":
        //     self.apiBaseUrl      = 'https://localhost:5001/api/onsched/consumer';
        //     self.setupApiBaseUrl = 'https://localhost:5001/api/onsched/setup';
        //     break;
        case "dev":
            self.apiBaseUrl      = 'https://dev-js.onsched.com/api/onsched/consumer';
            self.setupApiBaseUrl = 'https://dev-js.onsched.com/api/onsched/setup';
            break;
        default:
            self.apiBaseUrl      = 'https://sandbox-js.onsched.com/api/onsched/consumer';
            self.setupApiBaseUrl = 'https://sandbox-js.onsched.com/api/onsched/setup';
            break;
    }

    // Elements method that creates elements instance
    self.elements = function () {
        var elements = {};
        elements.objectName = "OnSched.elements";
        // elements.create method for creating an element
        elements.create = function (type, params, options) {
            var element = {};
            element.onsched = self;
            element.objectName = "OnSched.element";
            element.type = type;

            // check for params based on type? e.g. date is not relevant for all types
            element.params = params;
            element.options = options;

            ///
            /// element.mount method for mounting the created element in the DOM
            /// Much happens in the mount. API calls and templating of html.
            /// Bulk of exceptions likely to happen here.
            ///

            element.mount = function (id) {

                try {
                    element.id = id;

                    var el = document.getElementById(id);
                    
                    // Add theme class to element
                    el.className = `onsched-theme-${self.theme} sm md lg`;

                    // TODO - raise app error event
                    if (el === null)
                        throw "OnSched.mount: Element with id=" + id + " doesn't exist.";

                    let elementName = element.type.charAt(0).toUpperCase() 
                                    + element.type.slice(1) 
                                    + 'Element'

                    // Add size to element
                    var breakpointObserver = new ResizeObserver(entries => {
                        for (let entry of entries) {
                          // Add resize event for using OnSched.js Elements in an iFrame
                          OnSchedHelpers.UtilityCustomEvents.ResizeElement(element, entry.contentRect)

                            if (entry.contentRect.width >= 992) {
                                if (!el.classList.contains('lg')) {
                                    el.classList.add('lg')
                                }
                                if (!el.classList.contains('md')) {
                                    el.classList.add('md')
                                }
                                if (!el.classList.contains('sm')) {
                                    el.classList.add('sm')
                                }
                            }
                            else if (entry.contentRect.width >= 768) {
                                if (el.classList.contains('lg')) {
                                    el.classList.remove('lg')
                                }
                                if (!el.classList.contains('md')) {
                                    el.classList.add('md')
                                }
                                if (!el.classList.contains('sm')) {
                                    el.classList.add('sm')
                                }
                            }
                            else {
                                if (el.classList.contains('lg')) {
                                    el.classList.remove('lg')
                                }
                                if (el.classList.contains('md')) {
                                    el.classList.remove('md')
                                }
                                if (!el.classList.contains('sm')) {
                                    el.classList.remove('sm')
                                }
                            }
                        }
                    });

                    breakpointObserver.observe(el);

                    window.onresize = () => OnSchedHelpers.RepositionModalOnResize(element);

                    if (!self.authorized) {
                        OnSchedRest.Authorize(self)
                            .then(authorized => {
                                self.authorized = authorized;
                                OnSchedMount[elementName](this);
                        });
                    }
                    else {
                        OnSchedMount[elementName](this);
                    }

                } catch (e) {
                    // TODO - raise error event to the app client
                    console.log("OnSched.mount failed id=" + element.id + " type=" + element.type);
                    console.log(e);
                }
            };

            element.update = function (params, options) {
                // TODO - generate error here for client
                if (typeof params !== "object")
                    return;
                element.params = params != null ? params : element.params;
                element.options = options != null ? options : element.options;
                // I need to update DOM elements in cases like the search element
                if (element.type == "search") {
                    var elSearchText = document.querySelector(".onsched-search-form input[name=searchText]");
                    elSearchText.value = params.searchText !== null ? params.searchText : elSearchText.value;
                    elSearchText.placeholder = params.placeholder !== null ? params.placeholder : elSearchText.placeholder;
                    var elMessage = document.querySelector(".onsched-search-form p");
                    elMessage.textContent = params.message !== null ? params.message : elMessage.textContent;
                }
            };

            element.onChange = function (event) {
            };

            element.onClick = function (event) {
                if (event.target.classList.contains("day"))
                    OnSchedOnClick.CalendarDay(event, element);
                else
                if (event.target.classList.contains("time"))
                    OnSchedOnClick.AvailableTime(event, element);
                else
                if (event.target.classList.contains("list-item"))
                    OnSchedOnClick.ListItem(event, element);
                else
                if (event.target.classList.contains("month-prev"))
                    OnSchedOnClick.MonthPrev(event, element);
                else
                if (event.target.classList.contains("month-next"))
                    OnSchedOnClick.MonthNext(event, element);
//                if (event.target.classList.contains("btn-submit") == false)
//                    event.preventDefault();
            };

            element.handleException = function (e) {
                // Send some feedback to the user
            };

            return element; // return a reference to the element object for chaining
        };


        return elements; // return a reference to the elements object for chaining
    };

    self.logError = function (error) {
        // Capture exception
    };

    return self; // return reference to the OnSched object for chaining
}

export {
    OnSched,
    OnSchedHelpers,
    OnSchedTemplates,
    OnSchedRest
}
